import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { RESTAURANT_INFO } from "../data/menu";
import { useOpenStatus } from "../hooks/useOpenStatus";

const LOGO_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_popeye-1/artifacts/1mefxz8t_7ebcb42d-9879-4ac1-aa69-2d4dfd890347.jpg";

const NAV_LINKS = [
  { href: "#sobre-nosotros", label: "Nosotros" },
  { href: "#galeria", label: "Galería" },
  { href: "#menu", label: "Menú" },
  { href: "#contacto", label: "Contacto" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const isOpen = useOpenStatus(
    RESTAURANT_INFO.hours.openHour,
    RESTAURANT_INFO.hours.closeHour,
  );
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <a
          href="#hero"
          data-testid="logo-link"
          className="flex items-center gap-2.5 group"
        >
          <span className="w-[62px] h-[62px] sm:w-[70px] sm:h-[70px] rounded-full overflow-hidden border border-white/15 bg-black shadow-[0_4px_12px_-2px_rgba(220,38,38,0.35)] shrink-0 transition-transform group-hover:scale-105">
            <img
              src={LOGO_URL}
              alt="Mariscos Popeye 1985"
              className="w-full h-full object-cover"
            />
          </span>
          <div className="leading-none hidden sm:block">
            <p className="font-display text-white text-base sm:text-lg tracking-tight uppercase">
              Mariscos <span className="text-red-500">Popeye</span>
            </p>
            <p className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">
              Desde 1985 · Chihuahua
            </p>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className="text-sm uppercase tracking-widest text-white/70 hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="open-status-badge"
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10"
          >
            <span className="relative flex h-2 w-2">
              <span
                className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  isOpen ? "bg-green-500 animate-ping" : "bg-red-500"
                }`}
              />
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  isOpen ? "bg-green-500" : "bg-red-500"
                }`}
              />
            </span>
            <span className="text-[11px] uppercase tracking-widest text-white">
              {isOpen ? "Abierto ahora" : "Cerrado"}
            </span>
          </div>

          <a
            href={`tel:+52${RESTAURANT_INFO.phone}`}
            data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm uppercase tracking-widest px-4 py-2 transition-all hover:-translate-y-0.5"
          >
            <Phone size={16} strokeWidth={2.5} />
            Llamar
          </a>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-white"
            aria-label="Abrir menú"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-black/95 border-t border-white/10"
        >
          <nav className="px-4 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className="text-base uppercase tracking-widest text-white/80 hover:text-red-500 transition-colors py-2 border-b border-white/5"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <span className="relative flex h-2 w-2">
                <span
                  className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    isOpen ? "bg-green-500 animate-ping" : "bg-red-500"
                  }`}
                />
                <span
                  className={`relative inline-flex rounded-full h-2 w-2 ${
                    isOpen ? "bg-green-500" : "bg-red-500"
                  }`}
                />
              </span>
              <span className="text-xs uppercase tracking-widest text-white/70">
                {isOpen ? "Abierto ahora" : "Cerrado"} ·{" "}
                {RESTAURANT_INFO.hours.label}
              </span>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
